'use client';

import { Box, Button, Typography, useTheme } from '@mui/material';
import Link from 'next/link';
import React from 'react';

import GlobalBlockRenderer, {
  prefixClasses,
  processCustomClassesFromText,
} from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiImage } from '@/components/Utility/StrapiImage';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksStepItem,
  ComponentBlocksStepsList,
  Enum_Componentblocksstepslist_Stepsliststyle,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

export function StepsListTable({ blocks }: { blocks: ComponentBlocksStepsList }) {
  const theme = useTheme();

  const steps = (blocks?.Steps ?? []).filter(
    (step): step is ComponentBlocksStepItem => step != null
  );

  if (!steps.length) return null;

  return (
    <CustomBackground
      backgroundOptions={(blocks?.StepsListBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (blocks?.StepsListDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'sm',
        }) as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'StepsListTable',
      }}
    >
      {(blocks?.Title || blocks?.StepsListBlurb) && (
        <Box
          sx={{
            mb: 2,
            maxWidth: 'sm',
            mx: 'auto',
            justifyItems: 'center',
            textAlign: 'center',
            '> *': {
              textAlign: 'center',
            },
          }}
        >
          {blocks?.Title && (
            <Typography
              variant="h2"
              textAlign="center"
              fontWeight={700}
              sx={{
                color: theme.palette.common.black,
                pb: { xs: 3, md: 4 },
                px: 2,
                maxWidth: '48rem',
                mx: 'auto',
                lineHeight: 1.25,
              }}
            >
              {blocks.Title}
            </Typography>
          )}
        </Box>
      )}

      {blocks?.StepsListBlurb && (
        <Box
          sx={{
            mb: 3,
          }}
        >
          <GlobalBlockRenderer content={blocks.StepsListBlurb || []} />
        </Box>
      )}

      <Box
        sx={() => ({
          alignItems: 'center',
          alignContent: 'center',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: 6,
          rowGap: 4,
        })}
      >
        {steps.map((step, index) => (
          <StepItem
            key={step.id}
            step={step}
            index={index}
            listStyle={
              blocks.StepsListStyle ?? Enum_Componentblocksstepslist_Stepsliststyle.NumberedTable
            }
            StepsListShowNumbers={
              blocks?.StepsListStyle === Enum_Componentblocksstepslist_Stepsliststyle.NumberedTable
                ? false
                : (blocks.StepsListShowNumbers ?? false)
            }
          />
        ))}
      </Box>
    </CustomBackground>
  );
}

function StepItem({
  step,
  index,
  listStyle,
  StepsListShowNumbers,
}: {
  step: ComponentBlocksStepItem;
  index: number;
  listStyle: Enum_Componentblocksstepslist_Stepsliststyle;
  StepsListShowNumbers: boolean | undefined;
}) {
  const theme = useTheme();
  const cta = step.StepItemCta;
  const hasCta = cta && cta.ShowButton && cta.ButtonText;
  const isImageTable = listStyle === Enum_Componentblocksstepslist_Stepsliststyle.ImageTable;
  const isImageTableWithNumbers = isImageTable && StepsListShowNumbers === true;
  return (
    <>
      <Box
        className={`StepTableItem StepTableItem__col1 ${listStyle}`}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {/* Step Number */}
        {isImageTable && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              minWidth: '80px',
              minHeight: '80px',
              width: '100%',
              maxWidth: '200px',
              maxHeight: '200px',
              mx: 'auto',
              mr: isImageTableWithNumbers ? 4 : 'auto',
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              },
            }}
          >
            {step?.Image && (
              <StrapiImage image={step.Image} size="logo" imageWidth={80} imageHeight={80} />
            )}
          </Box>
        )}
        {!isImageTable && (
          <Box
            sx={{
              backgroundColor: `${theme.palette.brand.primary}`,
              color: theme.palette.common.white,
              height: '4rem',
              width: '4rem',
              lineHeight: 1,
              padding: 'xs',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2rem' },
                color: theme.palette.common.white,
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              {index + 1}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        className={`StepTableItem StepTableItem__col2 ${listStyle}`}
        sx={{
          display: 'flex',
          alignItems: isImageTableWithNumbers ? 'start' : 'center',
          justifyContent: isImageTableWithNumbers ? 'start' : 'space-between',
          gap: isImageTableWithNumbers ? 2 : 4,
        }}
      >
        {/* Step Number */}
        {isImageTableWithNumbers && (
          <Box
            sx={{
              backgroundColor: `${theme.palette.purple['200']}`,
              height: '2.5rem',
              width: '2.5rem',
              minWidth: '2.5rem',
              lineHeight: 1,
              padding: 'xs',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="large"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2rem' },
                color: theme.palette.brand.primary,
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              {index + 1}
            </Typography>
          </Box>
        )}
        {/* Step Image */}
        <Box sx={{ minWidth: 0 }}>
          {/* Step Title */}
          {step?.Title &&
            (() => {
              const { classes, cleanedText } = processCustomClassesFromText(step.Title);
              return (
                <Typography
                  variant="h5"
                  className={classes.length > 0 ? prefixClasses(classes) : undefined}
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.6rem' },
                    lineHeight: 1.3,
                    color: theme.palette.common.black,
                  }}
                >
                  {cleanedText}
                </Typography>
              );
            })()}

          {/* Step Description */}
          {step?.Description && (
            <Box sx={{ mb: hasCta ? 2 : 0 }}>
              <GlobalBlockRenderer
                content={step.Description}
                overrides={{
                  blocks: {
                    paragraph: ({ children }: { children: React.ReactNode }) => (
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {children}
                      </Typography>
                    ),
                  },
                }}
              />
            </Box>
          )}

          {/* CTA Button */}
          {hasCta && (
            <Box sx={{ mt: 1 }}>
              {cta.ButtonLink ? (
                cta.ButtonLink.startsWith('http') ? (
                  <Button
                    variant="contained"
                    color="primary"
                    href={cta.ButtonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {cta.ButtonText}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    href={cta.ButtonLink}
                  >
                    {cta.ButtonText}
                  </Button>
                )
              ) : (
                <Button variant="contained" color="primary">
                  {cta.ButtonText}
                </Button>
              )}
            </Box>
          )}
        </Box>
        {!isImageTable && step?.Image && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              minWidth: '80px',
              minHeight: '80px',
              width: '100%',
              maxWidth: '200px',
              maxHeight: '200px',
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              },
            }}
          >
            <StrapiImage image={step.Image} size="logo" imageWidth={80} imageHeight={80} />
          </Box>
        )}
      </Box>
    </>
  );
}
